import { registerApplication, start } from "single-spa";
import {
  constructApplications,
  constructRoutes,
  constructLayoutEngine,
} from "single-spa-layout";
import microfrontendLayout from "./microfrontend-layout.html";
import { HTMLLayoutData } from "single-spa-layout/dist/types/isomorphic/constructRoutes";
import {
  G_SASS_API_URL,
  MARKETPLACE_CONFIG_STORAGE_KEY,
  saveLocalData,
  isCognito,
} from "@gebeya/g-saas-core-mf-shared-ts";
import {
  MarketplaceConfigTools,
  ThirdPartyTools,
} from "./models/marketplace-config-tools.model";

const data: HTMLLayoutData = {
  loaders: {
    mfLoader: `<div class="lds-roller"><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div></div>`,
  },
  props: {},
};

const routes = constructRoutes(microfrontendLayout, data);
const applications = constructApplications({
  routes,
  loadApp({ name }) {
    return System.import(name);
  },
});
const layoutEngine = constructLayoutEngine({ routes, applications });

applications.forEach(registerApplication);
layoutEngine.activate();

/**
 * Setup tools such as google tag manager, or hubspot
 * NOTE: Google Tag Manager is capable of embedding other tools scripts such as,
 * google analytics script, hotjar, hubspot, or any other tracking scripts via the
 * tag manager console in the centralized manner. However, to give tenants more flexibility,
 * the tools are setup separately here.
 * @param thirdPartyTools The key-value pairs provided by the tenant config.
 */
const setupThridPartyTools = (thirdPartyTools: ThirdPartyTools) => {
  if (!thirdPartyTools) return;
  if (thirdPartyTools.hubspotTrackingId) {
    setupHubSpot(thirdPartyTools.hubspotTrackingId);
  }
  if (thirdPartyTools.microsoftClarityId) {
    setupMicrosoftClarity(thirdPartyTools.microsoftClarityId);
  }
  if (thirdPartyTools.intercomAppId) {
    setupIntercom(thirdPartyTools.intercomAppId);
  }
  thirdPartyTools.googleTagId
    ? setupGoogleTagManager(thirdPartyTools.googleTagId)
    : setupGoogleAnalytics(thirdPartyTools.googleAnalyticsId);
};

const setupHubSpot = (hubSpotId: string) => {
  let hubspotScript = document.createElement("script");
  hubspotScript.type = "text/javascript";
  hubspotScript.id = "hs-script-loader";
  hubspotScript.async = true;
  hubspotScript.defer = true;
  hubspotScript.src = `https://js.hs-scripts.com/${hubSpotId}.js`;
  document.body.appendChild(hubspotScript);
};

const setupGoogleTagManager = (googleTagId: string) => {
  const initializeGoogleTagManager = () => {
    const googleTagScript = document.createElement("script");
    googleTagScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${googleTagId}');`;
    document.head.appendChild(googleTagScript);
  };
  const insertGoogleTagNoscript = () => {
    const googleTagNoscript = document.createElement("noscript");
    let iframe = document.createElement("iframe");
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${googleTagId}`;
    iframe.style.cssText =
      "height: 0; width: 0; display: none; visibility: hidden;";
    googleTagNoscript.appendChild(iframe);
    document.body.insertBefore(googleTagNoscript, document.body.firstChild);
  };

  initializeGoogleTagManager();
  insertGoogleTagNoscript();
};

const setupGoogleAnalytics = (googleAnalyticsId: string) => {
  let googleAnalyticsScript = document.createElement("script");
  googleAnalyticsScript.type = "text/javascript";
  googleAnalyticsScript.async = true;
  googleAnalyticsScript.defer = true;
  googleAnalyticsScript.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`;
  document.head.appendChild(googleAnalyticsScript);
};

const setupMicrosoftClarity = (microsoftClarityId: string) => {
  let microsoftClarityScript = document.createElement("script");
  microsoftClarityScript.type = "text/javascript";
  microsoftClarityScript.innerHTML = `(function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "${microsoftClarityId}");`;
  document.head.appendChild(microsoftClarityScript);
};

const setupIntercom = (intercomAppId: string) => {
  const initializeIntercom = () => {
    let intercomScript = document.createElement("script");
    intercomScript.innerHTML = `(function(){
      var w=window;var ic=w.Intercom;if(typeof ic==="function"){
      ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){
      i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){
      var s=d.createElement('script');s.type='text/javascript';s.async=true;
      s.src='https://widget.intercom.io/widget/${intercomAppId}';
      var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};
      if(document.readyState==='complete'){l();}else if(w.attachEvent){
      w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}
      }})();`;
    document.head.appendChild(intercomScript);
  };

  const bootUpIntercomForLoggedOutUser = () => {
    let intercomBootScript = document.createElement("script");
    intercomBootScript.innerHTML = `window.Intercom('boot', {
        app_id: '${intercomAppId}'
      });`;
    document.head.appendChild(intercomBootScript);
  };
  initializeIntercom();
  bootUpIntercomForLoggedOutUser();
};

const setRouteValidation = (_isCognito) => {
  // check route to load the correct micro frontend
  const route = window.location.pathname;
  const path = route
    .replace(location.protocol + "//" + location.host, "")
    .toLowerCase();
  if (
    _isCognito &&
    (path.startsWith("/auth") ||
      path.startsWith("/gigworker") ||
      path.startsWith("/register"))
  ) {
    location.href = "/"; //redirect to home
  }
};

const setupMetaTags = (marketPlaceConfig: MarketplaceConfigTools) => {
  const metaTags = [
    { name: "og:locale", content: "en_US" },
    { name: "og:type", content: "website" },
    { name: "og:title", content: marketPlaceConfig.companyName },
    {
      name: "og:description",
      content: marketPlaceConfig.tagContent,
    },
    { name: "og:url", content: location.href },
    { name: "og:site_name", content: marketPlaceConfig.companyName },
    { name: "og:image", content: marketPlaceConfig.marketPlaceDesign.logoUrl },
    {
      name: "og:image:secure_url",
      content: marketPlaceConfig.marketPlaceDesign.logoUrl,
    },
    { name: "og:image:width", content: "1551" },
    { name: "og:image:height", content: "600" },
    { name: "og:image:alt", content: marketPlaceConfig.companyName },
    { name: "og:image:type", content: "image/png" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: marketPlaceConfig.companyName },
    {
      name: "twitter:description",
      content: marketPlaceConfig.tagContent,
    },
    {
      name: "twitter:image",
      content: marketPlaceConfig.marketPlaceDesign.logoUrl,
    },
  ];

  metaTags.forEach((metaTag) => {
    const element = document.createElement("meta");
    element.name = metaTag.name;
    element.content = metaTag.content;
    document.getElementsByTagName("head")[0].appendChild(element);
  });
};

const getConfig = () => {
  const fullHostBaseUrl = location.protocol + "//" + location.host;
  fetch(`${G_SASS_API_URL}/tenants/configuration`, {
    headers: { "x-tenant-origin": fullHostBaseUrl },
  })
    .then((response) => response.json())
    .then((config) => {
      if (!config?.data) {
        handleConfigurationError();
        return;
      }
      // Handle successful response
      saveLocalData(
        MARKETPLACE_CONFIG_STORAGE_KEY,
        JSON.stringify(config?.data)
      );
      setupAndStart(config);
    })
    .catch(() => handleConfigurationError());
};

const setupAndStart = (config: { data: any }) => {
  const data = config?.data;
  if (data) {
    setupThridPartyTools(data.platformConfiguration);
    setupMetaTags(data);
    window.addEventListener("single-spa:before-routing-event", (evt) => {
      setRouteValidation(isCognito(data));
    });
  }

  start();
};

const handleConfigurationError = () => {
  // if we have an error, just start the app and the wrapper micro frontend will handle the error
  start();
};

getConfig();
